import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { GitCompare, Trash2, ArrowRight, Info, Bookmark } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });
const money = (n) => (n == null ? '-' : `$${Math.round(n).toLocaleString()}`);
const v = (d) => (d && typeof d === 'object' && 'value' in d ? d.value : d);
const AI_HUE = {
  'Low Transformation': '#34d399', 'Moderate Transformation': '#38bdf8',
  'High Transformation': '#fb7185', 'Emerging Opportunity': '#a78bfa',
};

export default function ComparePathwaysPage() {
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  const [picked, setPicked] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSaved = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pathways/saved`, auth());
      const rows = r.data.pathways || [];
      setSaved(rows);
      setPicked((p) => (p.length ? p : rows.slice(0, 4).map((x) => x.id)));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  useEffect(() => {
    if (!picked.length) { setComparison(null); return; }
    axios.get(`${API}/pathways/compare?ids=${picked.join(',')}`, auth())
      .then((r) => setComparison(r.data)).catch(() => setComparison(null));
  }, [picked]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id)
    : p.length >= 5 ? p : [...p, id]));

  const remove = async (id) => {
    await axios.delete(`${API}/pathways/saved/${id}`, auth());
    setPicked((p) => p.filter((x) => x !== id));
    loadSaved();
  };

  if (loading) return <AppLayout><LoadingSpinner /></AppLayout>;

  const rows = comparison?.pathways || [];

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="compare-pathways-page">
        <div className="relative overflow-hidden rounded-2xl border border-violet-300/20 bg-[#070a1c] px-6 py-8">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.20), transparent 65%)' }} />
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-300/35 bg-violet-400/[0.08] px-3 py-1
            text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-200 shadow-[0_0_26px_-10px_#a78bfa]">
            <GitCompare className="h-3 w-3" /> Compare my futures
          </span>
          <h1 className="font-outfit mt-4 text-3xl font-semibold text-white sm:text-4xl">
            Four futures,{' '}
            <span className="bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">one screen</span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-300/90">
            Put your saved paths side by side on cost, pay, opportunity and how AI is changing each one.
            Nothing is blended into a single score, the trade-off is the point.
          </p>
        </div>

        <GlassCard className="p-5">
          <p className="text-[11px] uppercase tracking-wider text-slate-300">Your saved pathways · pick up to five</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {saved.map((p) => {
              const on = picked.includes(p.id);
              return (
                <span key={p.id} className="group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11.5px] transition-all"
                  style={{
                    borderColor: on ? '#a78bfa88' : 'rgba(148,163,184,0.22)',
                    background: on ? 'rgba(167,139,250,0.12)' : 'transparent',
                    color: on ? '#ddd6fe' : '#cbd5e1',
                  }}>
                  <button onClick={() => toggle(p.id)} data-testid={`pick-${p.id}`}>{p.identity.pathway_name}</button>
                  <button onClick={() => remove(p.id)} data-testid={`remove-${p.id}`}
                    className="opacity-0 transition-opacity group-hover:opacity-100" title="Remove">
                    <Trash2 className="h-3 w-3 text-slate-300 hover:text-rose-300" />
                  </button>
                </span>
              );
            })}
            {!saved.length && (
              <button onClick={() => navigate('/app/pathways')} data-testid="go-find-pathways"
                className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300/40 px-3 py-1.5 text-[11.5px] text-cyan-200">
                <Bookmark className="h-3 w-3" /> Save a pathway first <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </GlassCard>

        {!!rows.length && (
          <GlassCard className="overflow-x-auto p-5 shadow-[0_0_70px_-45px_#a78bfa]" data-testid="compare-table">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="pb-3 pr-4 text-[10.5px] uppercase tracking-wider text-slate-300">Dimension</th>
                  {rows.map((p) => (
                    <th key={p.id} className="pb-3 pr-4">
                      <p className="text-xs font-semibold text-white">{p.name}</p>
                      <p className="text-[10.5px] capitalize text-slate-300">{p.type.replace(/_/g, ' ')}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-[11.5px]">
                <Row label="Field of study" cells={rows.map((p) => p.field || '-')} />
                <Row label="Institutions offering it" cells={rows.map((p) => p.institutions ? p.institutions.toLocaleString() : '-')} />
                <Row label="Entry credential" cells={rows.map((p) => p.entry_level ? `${p.entry_level.label}` : (p.entry_education || '-'))} />
                <Row label="Earnings at entry level"
                  cells={rows.map((p) => p.entry_level ? money(p.entry_level.earnings) : '-')} tone="cyan" />
                <Row label="Earnings at highest level"
                  cells={rows.map((p) => p.best_level ? `${money(p.best_level.earnings)} (${p.best_level.label})` : '-')} tone="cyan" />
                <Row label="Target occupation" cells={rows.map((p) => p.occupation || '-')} />
                <Row label="Occupational median"
                  cells={rows.map((p) => money(v(p.wages?.median)))} tone="cyan" />
                <Row label="Middle-half range"
                  cells={rows.map((p) => (v(p.wages?.p25) && v(p.wages?.p75)
                    ? `${money(v(p.wages.p25))}, ${money(v(p.wages.p75))}` : '-'))} />
                <Row label="Opportunity Score"
                  cells={rows.map((p) => (p.opportunity_score ? `${v(p.opportunity_score)} / 100` : '-'))} tone="cyan" />
                <Row label="AI transformation"
                  cells={rows.map((p) => (p.ai_impact ? v(p.ai_impact).outlook : '-'))}
                  hues={rows.map((p) => (p.ai_impact ? AI_HUE[v(p.ai_impact).outlook] : undefined))} />
                <Row label="Job outlook"
                  cells={rows.map((p) => (p.outlook ? `${v(p.outlook.growth_pct) > 0 ? '+' : ''}${v(p.outlook.growth_pct)}% to 2034` : '-'))} />
              </tbody>
            </table>
            <p className="mt-4 flex items-start gap-2 text-[10.5px] leading-relaxed text-slate-300">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {comparison.note} Programme earnings come from the
              College Scorecard; occupational wages and outlook from BLS; Opportunity and AI transformation are our
              own labelled models.
            </p>
          </GlassCard>
        )}
      </div>
    </AppLayout>
  );
}

const Row = ({ label, cells, tone, hues = [] }) => (
  <tr className="border-b border-white/[0.06] transition-colors hover:bg-violet-400/[0.04]">
    <td className="py-2.5 pr-4 text-slate-300">{label}</td>
    {cells.map((c, i) => (
      <td key={i} className="py-2.5 pr-4"
        style={{ color: hues[i] || (tone === 'cyan' ? '#a5f3fc' : '#e2e8f0') }}>{c}</td>
    ))}
  </tr>
);
